/**
 * Admin Support Dashboard — case management for the CareerOS admin.
 *
 * Not routed for regular users. Guards behind auth.is_admin on the backend.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Search, ShieldAlert, RefreshCw, Send, MessageSquare, ChevronRight, X,
  CheckCircle2, AlertOctagon,
} from "lucide-react";

const STATUSES = ["open", "in_progress", "escalated", "resolved", "unresolved", "closed"];
const PRIORITIES = ["Critical", "High", "Medium", "Low"];
const CATEGORIES = [
  "general", "feature_help", "account", "login",
  "billing", "subscription", "technical", "bug_report",
  "ai_output", "privacy", "feedback", "feature_request", "other",
];

export default function AdminSupport() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [filters, setFilters] = useState({ status: "", priority: "", category: "", plan: "", q: "" });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const { data } = await api.get(`/support/admin/cases?${params.toString()}`);
      setCases(data.cases || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = () => {
    api.get("/support/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  };

  useEffect(() => { load(); loadAnalytics(); /* eslint-disable-next-line */ }, [filters]);

  // Guard after hooks so rules-of-hooks are respected.
  if (user && !user.is_admin) return <Navigate to="/app" replace />;

  const openCase = async (caseId) => {
    setSelected(caseId);
    setDetail(null);
    try {
      const { data } = await api.get(`/support/admin/case/${caseId}`);
      setDetail(data);
    } catch (e) {
      toast.error("Case load failed");
    }
  };

  const patch = async (updates) => {
    if (!selected) return;
    try {
      await api.patch(`/support/admin/case/${selected}`, updates);
      toast.success("Case updated");
      await openCase(selected);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const resendEmail = async () => {
    if (!selected) return;
    try {
      const { data } = await api.post(`/support/admin/case/${selected}/resend-email`);
      toast.success(data.ok ? "Email resent" : `Failed: ${data.error || "unknown"}`);
      await openCase(selected);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resend failed");
    }
  };

  return (
    <div className="max-w-7xl" data-testid="admin-support">
      <PageHeader
        eyebrow="Admin · Support"
        title="Support case management."
        subtitle="Triage, respond, and resolve escalated cases."
      />

      {/* Analytics strip */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
          <Stat label="Total" value={analytics.total} testid="stat-total" />
          <Stat label="Last 30d" value={analytics.last_30_days} testid="stat-30d" />
          <Stat label="Open" value={analytics.open} testid="stat-open" />
          <Stat label="Escalated" value={analytics.escalated} testid="stat-escalated" />
          <Stat label="Resolved" value={analytics.resolved} testid="stat-resolved" />
          <Stat label="Res. rate" value={`${analytics.resolution_rate_pct}%`} testid="stat-res-rate" />
        </div>
      )}

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-3 mb-4 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="filter-q"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder="Search case ID, email, or summary…"
            className="w-full pl-9 pr-3 py-1.5 bg-background border border-border rounded-md text-sm outline-none"
          />
        </div>
        <Select data-testid="filter-status" value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} label="Status" options={STATUSES} />
        <Select data-testid="filter-priority" value={filters.priority} onChange={(v) => setFilters({ ...filters, priority: v })} label="Priority" options={PRIORITIES} />
        <Select data-testid="filter-category" value={filters.category} onChange={(v) => setFilters({ ...filters, category: v })} label="Category" options={CATEGORIES} />
        <Select data-testid="filter-plan" value={filters.plan} onChange={(v) => setFilters({ ...filters, plan: v })} label="Plan" options={["free", "pro", "premium"]} />
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="filter-refresh">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
        </Button>
      </div>

      <div className="grid lg:grid-cols-[420px_1fr] gap-4">
        {/* Case list */}
        <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-2" data-testid="case-list">
          {cases.length === 0 && !loading && (
            <div className="text-sm text-muted-foreground text-center py-12">No cases match your filters.</div>
          )}
          {cases.map((c) => (
            <AdminCaseRow key={c.case_id} c={c} selected={selected === c.case_id} onClick={() => openCase(c.case_id)} />
          ))}
        </div>

        {/* Detail pane */}
        <div className="bg-card border border-border rounded-xl min-h-[70vh]" data-testid="case-detail">
          {!selected ? (
            <div className="p-8 text-sm text-muted-foreground text-center">Select a case to view details.</div>
          ) : !detail ? (
            <div className="p-8 text-sm text-muted-foreground text-center">Loading…</div>
          ) : (
            <CaseDetail detail={detail} onPatch={patch} onResend={resendEmail} onClose={() => setSelected(null)} />
          )}
        </div>
      </div>
    </div>
  );
}

function AdminCaseRow({ c, selected, onClick }) {
  const priorityColor = ({
    Critical: "bg-red-500 text-white",
    High: "bg-orange-500 text-white",
    Medium: "bg-yellow-400 text-black",
    Low: "bg-muted text-muted-foreground",
  })[c.priority] || "bg-muted text-muted-foreground";
  const statusIcon = c.status === "resolved" ? <CheckCircle2 size={12} className="text-[hsl(var(--success))]" />
    : c.status === "escalated" ? <ShieldAlert size={12} className="text-[hsl(var(--warning))]" />
    : c.status === "unresolved" ? <AlertOctagon size={12} className="text-destructive" />
    : <MessageSquare size={12} />;
  return (
    <button
      onClick={onClick}
      data-testid={`case-row-${c.case_id}`}
      className={`w-full text-left p-3 rounded-lg border ${selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"} bg-card`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono uppercase ${priorityColor}`}>{c.priority}</span>
        <span className="text-[10px] font-mono text-muted-foreground">{c.case_id}</span>
        <span className="ml-auto text-[10px] font-mono text-muted-foreground flex items-center gap-1">{statusIcon} {c.status}</span>
      </div>
      <div className="text-sm truncate">{c.summary || "(no summary yet)"}</div>
      <div className="text-[10px] text-muted-foreground mt-1 truncate">
        {c.user_email || "unknown"} · {c.user_plan || "free"} · {c.category} · {new Date(c.created_at).toLocaleString()}
      </div>
    </button>
  );
}

function CaseDetail({ detail, onPatch, onResend, onClose }) {
  const c = detail.case;
  const msgs = detail.messages || [];
  const [note, setNote] = useState(c.admin_notes || "");

  return (
    <div className="p-4 h-full flex flex-col">
      <div className="flex items-center gap-3 mb-3">
        <div>
          <div className="text-[10px] font-mono uppercase text-muted-foreground">{c.case_id}</div>
          <h2 className="text-lg font-display font-bold">{c.summary || "(no summary)"}</h2>
        </div>
        <button onClick={onClose} className="ml-auto p-1.5 rounded hover:bg-muted"><X size={14} /></button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 text-[11px]">
        <Info label="User" value={c.user_email} />
        <Info label="Plan" value={c.user_plan} />
        <Info label="Category" value={c.category} />
        <Info label="Page" value={c.page_affected || "-"} />
        <Info label="Browser" value={(c.browser || "").split(" ")[0] || "-"} />
        <Info label="Device" value={c.device || "-"} />
        <Info label="Confidence" value={c.agent_confidence != null ? c.agent_confidence.toFixed(2) : "-"} />
        <Info label="Email sent" value={c.email_sent ? new Date(c.email_sent_at).toLocaleString() : (c.email_error || "no")} />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <QuickPill label="Status" testid="patch-status" value={c.status} options={STATUSES} onChange={(v) => onPatch({ status: v })} />
        <QuickPill label="Priority" testid="patch-priority" value={c.priority} options={PRIORITIES} onChange={(v) => onPatch({ priority: v })} />
        <Button size="sm" variant="outline" onClick={onResend} data-testid="admin-resend-email" className="gap-1">
          <Send size={12} /> {c.email_sent ? "Resend email" : "Send escalation email"}
        </Button>
      </div>

      {c.recommended_admin_action && (
        <div className="mb-3 rounded-lg bg-[hsl(var(--warning))/0.1] border border-[hsl(var(--warning))/0.3] p-3 text-xs">
          <div className="font-mono uppercase text-[9px] text-[hsl(var(--warning))] mb-1">Agent-recommended action</div>
          {c.recommended_admin_action}
        </div>
      )}

      {/* Transcript */}
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Transcript</div>
      <div className="flex-1 overflow-y-auto space-y-2 border border-border rounded-lg p-3 bg-background/40" data-testid="admin-transcript">
        {msgs.map((m, i) => (
          <div key={`${m.created_at}-${i}`} className={`text-sm ${m.role === "user" ? "text-emerald-500" : "text-foreground"}`}>
            <span className="text-[10px] font-mono opacity-60">{m.role.toUpperCase()} · {new Date(m.created_at).toLocaleTimeString()}</span>
            <div className="whitespace-pre-wrap">{m.content}</div>
          </div>
        ))}
      </div>

      {/* Admin notes */}
      <div className="mt-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Admin notes (internal)</div>
        <textarea
          data-testid="admin-notes"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => note !== c.admin_notes && onPatch({ admin_notes: note })}
          rows={2}
          className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm"
        />
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-card border border-border rounded-xl p-3" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-xl font-black mt-1">{value ?? "—"}</div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-medium truncate">{value || "-"}</div>
    </div>
  );
}

function Select({ label, value, onChange, options, ...rest }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      {...rest}
      className="bg-background border border-border rounded-md px-2 py-1.5 text-xs outline-none"
    >
      <option value="">{`${label}: any`}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function QuickPill({ label, value, options, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      {label}
      <select
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="bg-background border border-border rounded-md px-2 py-1 text-xs"
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
